import {
  faPencil,
  faPlus,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { API_URL } from "../../config"; // Import API_URL from config
import LoadingSpinner from "../../components/LoadingSpinner";
import { showToast } from "../../components/toast";
import { PermissionWrapper } from "../../helpers/permission.js";

const CustomPagesList = () => {
  const [customPages, setCustomPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);
  const [error, setError] = useState(null);
  const [delId, setDelId] = useState(0);
  const navigate = useNavigate();
  const toastShownRef = useRef(false);

  // 🔎 Filters
  const [titleFilter, setTitleFilter] = useState("");
  const [headingFilter, setHeadingFilter] = useState("");
  const [descFilter, setDescFilter] = useState("");

  // 📑 Pagination states
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  // Fetch custom pages on component mount
  useEffect(() => {
    fetchCustomPages();
  }, []);

  const fetchCustomPages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      const response = await axios.get(`${API_URL}CustomPage`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("API Response:", response.data);

      if (!toastShownRef.current) {
        showToast.success("Custom Pages fetched successfully");
        toastShownRef.current = true;
      }

      let pagesData = [];
      if (response.data?.pages && Array.isArray(response.data.pages)) {
        pagesData = response.data.pages;
      } else if (response.data?.success && response.data?.data) {
        pagesData = response.data.data;
      } else if (Array.isArray(response.data)) {
        pagesData = response.data;
      } else {
        pagesData = response.data?.data || response.data || [];
      }

      setCustomPages(pagesData);
      setTotal(pagesData.length);
      setPageCount(Math.ceil(pagesData.length / limit));
      setLoading(false);
    } catch (err) {
      console.error("Error fetching custom pages:", err);
      setError("Failed to fetch custom pages. Please try again later.");
      setLoading(false);
    }
  };

  // 🔎 Filtered Pages
  const filteredPages = useMemo(() => {
    return customPages.filter((page) => {
      if (
        titleFilter &&
        !(page.seo_title || page.title || "")
          .toLowerCase()
          .includes(titleFilter.toLowerCase())
      ) {
        return false;
      }

      if (
        headingFilter &&
        !(page.page_name || page.heading || "")
          .toLowerCase()
          .includes(headingFilter.toLowerCase())
      ) {
        return false;
      }

      if (
        descFilter &&
        !(page.seo_desc || page.short_desc || "")
          .toLowerCase()
          .includes(descFilter.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [customPages, titleFilter, headingFilter, descFilter]);

  // 📑 Update pagination when filters change
  useEffect(() => {
    setTotal(filteredPages.length);
    setPageCount(Math.ceil(filteredPages.length / limit));
    setPage(1);
  }, [filteredPages, limit]);

  useEffect(() => {
    if (!customPages.length) return;

    setIsFiltering(true);

    const timer = setTimeout(() => {
      setIsFiltering(false);
    }, 300); // smooth UX delay

    return () => clearTimeout(timer);
  }, [titleFilter, headingFilter, descFilter]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedPages = filteredPages.slice(start - 1, end);

  const handleDelete = async (id) => {
    setDelId(id);
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this Record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("authToken");

          const response = await axios.delete(`${API_URL}CustomPage/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          });

          console.log("Delete response:", response);

          if (response.status === 200 || response.data.success) {
            Swal.fire("Deleted!", "Your record has been deleted.", "success");
            fetchCustomPages();
          } else {
            Swal.fire(
              "Error",
              response.data.message || "Failed to delete the page.",
              "error"
            );
          }
        } catch (err) {
          console.error("Error deleting page:", err);
          Swal.fire(
            "Error",
            err.response?.data?.message || "Failed to delete the page.",
            "error"
          );
        }
      }
    });
  };

  const handleRetry = () => {
    setError(null);
    fetchCustomPages();
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setTitleFilter("");
      setHeadingFilter("");
      setDescFilter("");
    };
    window.addEventListener('clearCustomPageFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearCustomPageFilterEvent', handleClearFilters);
    };
  }, []);

  return (
    <div className="page-container mt-5">
      {/* {loading && <LoadingSpinner />} */}
      <div className="">
        {error && (
          <div className="alert alert-danger">
            {error}
            <button
              className="btn btn-sm btn-outline-danger ml-2"
              onClick={handleRetry}
            >
              Retry
            </button>
          </div>
        )}

        <div className="panel panel-default with-rounded-corner with-shadow p-10">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="panel-heading border-0">
              <h4 className="panel-title">Manage Custom Pages</h4>
            </div>
            <div className="d-flex align-items-center">
              <PermissionWrapper module="policy_pages" action="add">
                <button
                  className="btn btn-default btn-sm"
                  onClick={() => navigate(`/CustomPages/add`)}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Page
                </button>
              </PermissionWrapper>

              {/* 📑 Pagination Controls */}
              {customPages.length > 0 && (
                <div className="d-inline-block me-3 ms-3">
                  <span style={{ fontSize: "0.8rem" }} className="me-2">
                    {start} to {end} of {total}
                  </span>
                  <button
                    className="btn btn-default btn-sm"
                    title="Previous"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  &nbsp;
                  <button
                    className="btn btn-default btn-sm"
                    title="Next"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === pageCount}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {loading || customPages.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-condensed table-bordered bg-white">
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>#</th>
                    <th>Title</th>
                    <th>Heading</th>
                    <th>Short Description</th>
                    <th>Action</th>
                  </tr>
                  {/* 🔎 Search Filters Row */}
                  <tr>
                    <th></th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search title..."
                        value={titleFilter}
                        onChange={(e) => setTitleFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search heading..."
                        value={headingFilter}
                        onChange={(e) => setHeadingFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search description..."
                        value={descFilter}
                        onChange={(e) => setDescFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <button
                        className="btn btn-default btn-sm"
                        onClick={() => {
                          setTitleFilter("");
                          setHeadingFilter("");
                          setDescFilter("");
                        }}
                        title="Clear filters"
                      >
                        Clear
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading || isFiltering ? (
                    Array.from({ length: 10 }).map((_, i) => (
                      <tr key={i}>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                      </tr>
                    ))
                  ) : paginatedPages.length ? (
                    paginatedPages.map((pageItem, index) => (
                      <tr key={pageItem.id}>
                        <td style={{ textAlign: "center" }}>
                          {(page - 1) * limit + index + 1}
                        </td>
                        <td>{pageItem.seo_title || pageItem.title}</td>
                        <td>{pageItem.page_name || pageItem.heading}</td>
                        <td>{pageItem.seo_desc || pageItem.short_desc}</td>
                        <td>
                          <PermissionWrapper
                            module="policy_pages"
                            action="edit"
                          >
                            <button
                              className="mr-2 btn btn-warning btn-xs me-2"
                              onClick={() =>
                                navigate(`/CustomPages/edit/${pageItem.id}`)
                              }
                              title="Edit Custom Page"
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </button>
                          </PermissionWrapper>
                          <PermissionWrapper
                            module="policy_pages"
                            action="delete"
                          >
                            <button
                              className="cancel btn btn-danger btn-xs"
                              onClick={() => handleDelete(pageItem.id)}
                              title="Delete Custom Page"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </PermissionWrapper>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No pages match your search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            !loading && <p className="text-center py-4">No records found</p>
          )}
        </div>
      </div>

      {/* Scroll to top button */}
      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default CustomPagesList;
